import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { components } from '@aaimplatform/institutional-types/platform-admin';
import { platformAdminClient } from '../api/client';
import {
  integrationsApi,
  type ThirdPartyIntegration,
} from '../api/services/integrations';

/** POST /integrations/{integrationId}/test response (platform-admin.openapi.yaml). */
export type IntegrationTestResult = components['schemas']['IntegrationTestResult'];

// Sprint C17+1 (2026-05-04): useDataProviders / useRefreshProvider hooks
// removed alongside the deprecated PlatformDataProvider model. Use
// useThirdPartyIntegrations below for third-party integrations.

export function useThirdPartyIntegrations() {
  return useQuery<ThirdPartyIntegration[]>({
    queryKey: ['admin', 'integrations', 'third-party'],
    queryFn: () => integrationsApi.listThirdParty(),
  });
}

/**
 * Run a connection test and return its result.
 *
 * The route answers 200 with `{ success: false, error }` for a connection that
 * failed, so the body is the result; a status code alone would report every
 * failed connection as a pass. `integrationsApi.refreshThirdParty` discards the
 * body, so this mutation reads it through the typed client directly.
 */
export function useRefreshThirdParty() {
  const queryClient = useQueryClient();
  return useMutation<IntegrationTestResult, Error, string>({
    mutationFn: async (id: string) => {
      const { data, error } = await platformAdminClient.POST('/integrations/{integrationId}/test', {
        params: { path: { integrationId: id } },
      });
      if (error || !data) throw new Error('The connection test request failed');
      return data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'integrations', 'third-party'] });
    },
  });
}
